import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Plus,
  ArrowLeft,
  Image as ImageIcon,
  Eye,
  Sparkles,
  Layers,
  CheckSquare,
  X,
  Upload,
  Download,
  FileText,
  CheckCircle,
  History,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { ModuleItem } from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect from "../components/CustomSelect";
import RightAnswerSelect from "../components/RightAnswerSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import ImagePreviewModal from "../../../../app/shared/components/ImagePreviewModal";
import "../styles/QuestionBankStyles.css";

const AddQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialTab =
    location.state?.tab || searchParams.get("tab") === "upload"
      ? "upload"
      : "enter";

  // Mode Toggle: 'enter' (Default single question form) vs 'upload' (Bulk Excel Upload)
  const [activeTab, setActiveTab] = useState<"enter" | "upload">(initialTab);

  // Module & Shared State
  const [difficultyLevel, setDifficultyLevel] = useState<string>("Easy");
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [moduleId, setModuleId] = useState<string>("");
  const [moduleName, setModuleName] = useState<string>("");

  // Add Module Modal state
  const [showAddModuleModal, setShowAddModuleModal] = useState<boolean>(false);
  const [newModuleName, setNewModuleName] = useState<string>("");
  const [savingModule, setSavingModule] = useState<boolean>(false);

  // Live Preview toggle for single question
  const [showLivePreview, setShowLivePreview] = useState<boolean>(false);

  // Single Question Form State
  const [questionText, setQuestionText] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [concepts, setConcepts] = useState<string>("");
  const [optA, setOptA] = useState<string>("");
  const [optB, setOptB] = useState<string>("");
  const [optC, setOptC] = useState<string>("");
  const [optD, setOptD] = useState<string>("");
  const [correctOption, setCorrectOption] = useState<string>("A");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [previewModalSrc, setPreviewModalSrc] = useState<string | null>(null);

  // Bulk Upload Form State
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] =
    useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [targetQuestionId, setTargetQuestionId] = useState<string>("");
  const [uploadImageFile, setUploadImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  // Form Validations
  const isSingleFormValid = Boolean(
    moduleId &&
    questionText.trim() &&
    optA.trim() &&
    optB.trim() &&
    optC.trim() &&
    optD.trim() &&
    correctOption,
  );

  const isBulkUploadValid = Boolean(selectedExcelFile && moduleId);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchModules = async () => {
    try {
      const res = await QuestionBankClient.listModules();
      if (res && res.modules && res.modules.length > 0) {
        const modItems = res.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
        if (!moduleId) {
          setModuleId(modItems[0].id);
          setModuleName(modItems[0].name);
        }
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    setSavingModule(true);
    try {
      const res = await QuestionBankClient.createModule(newModuleName.trim());
      setShowAddModuleModal(false);
      setNewModuleName("");
      await fetchModules();
      if (res && res.module) {
        setModuleId(res.module.module_id);
        setModuleName(res.module.module_name);
      }
      setAlertConfig({
        show: true,
        title: "Module Created",
        message: `Module '${newModuleName}' created and selected successfully.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Error creating module:", err);
      setAlertConfig({
        show: true,
        title: "Module Creation Failed",
        message: err?.response?.data?.detail || "Failed to create module.",
        type: "error",
      });
    } finally {
      setSavingModule(false);
    }
  };

  // Single Question Submit
  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionText.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Question description is required.",
        type: "warning",
      });
      return;
    }
    if (!moduleId) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please select or create a Module.",
        type: "warning",
      });
      return;
    }
    if (!optA.trim() || !optB.trim() || !optC.trim() || !optD.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "All 4 Options (Option A, B, C, and D) are mandatory.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const answersPayload = [
        { answer: optA.trim(), correct_answer: correctOption === "A" },
        { answer: optB.trim(), correct_answer: correctOption === "B" },
        { answer: optC.trim(), correct_answer: correctOption === "C" },
        { answer: optD.trim(), correct_answer: correctOption === "D" },
      ];

      let uploadedImageUrl = undefined;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        formData.append("module_id", moduleId);
        const imgRes = await QuestionBankClient.uploadQuestionImage(formData);
        uploadedImageUrl = imgRes.image_url;
      }

      const payload = {
        question: questionText,
        question_type: "MCQ" as any,
        difficulty_level: difficultyLevel as any,
        industry_name: "Industry",
        module_id: moduleId,
        module_name: moduleName,
        explanation: explanation || undefined,
        concepts: concepts
          ? concepts
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined,
        answers: answersPayload,
        image_url: uploadedImageUrl,
      };

      const res = await QuestionBankClient.createQuestion(payload);
      setAlertConfig({
        show: true,
        title: "Success",
        message: res?.detail || "Question created successfully!",
        type: "success",
      });

      setTimeout(() => {
        navigate("/dashboard/question-bank");
      }, 1200);
    } catch (err: any) {
      console.error("Error creating question:", err);
      setAlertConfig({
        show: true,
        title: "Creation Failed",
        message: err?.response?.data?.detail || "Failed to create question.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Bulk Upload Template Download
  const handleDownloadTemplate = async (
    fileType: "excel" | "csv" = "excel",
  ) => {
    setDownloadingTemplate(true);
    try {
      const res = await QuestionBankClient.downloadTemplate("MCQ", fileType);
      if (res && res.file_path) {
        window.open(res.file_path, "_blank");
      } else {
        setAlertConfig({
          show: true,
          title: "Template Download",
          message: "Could not generate template download URL.",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error downloading template:", err);
      setAlertConfig({
        show: true,
        title: "Download Error",
        message: "Failed to download template file.",
        type: "error",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Bulk Upload Submit
  const handleBulkUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExcelFile) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please select an Excel file to upload.",
        type: "warning",
      });
      return;
    }
    if (!moduleId) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please select a Target Module for questions.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedExcelFile);
      formData.append("module_id", moduleId);
      formData.append("question_type", "MCQ");

      const res = await QuestionBankClient.uploadQuestionsFile(formData);

      setUploadResult(res);

      setAlertConfig({
        show: true,
        title: "Bulk Upload Complete",
        message: `Processed ${res.total_questions || 0} questions. ${res.success_count || 0} created successfully.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Error uploading file:", err);
      setAlertConfig({
        show: true,
        title: "Upload Failed",
        message:
          err?.response?.data?.detail ||
          "Failed to process bulk question file.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      setAlertConfig({
        show: true,
        title: "Error",
        message:
          "You can only upload 1 image per question. Please select a single image file.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      setAlertConfig({
        show: true,
        title: "Error",
        message:
          "Invalid file type. Only image files (PNG, JPG, JPEG, WEBP, SVG) are allowed.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Image file size exceeds the allowed limit of 5MB.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
        setAlertConfig({
          show: true,
          title: "Error",
          message: `Image dimensions exceed the allowed limit. Maximum allowed is ${MAX_WIDTH}x${MAX_HEIGHT}, but got ${img.width}x${img.height}.`,
          type: "error",
        });
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
        return;
      }
      setImageFile(file);
      setImagePreviewUrl(objectUrl);
    };
    img.onerror = () => {
      setAlertConfig({
        show: true,
        title: "Error",
        message:
          "Failed to load the selected image file. Please choose a valid image file.",
        type: "error",
      });
      URL.revokeObjectURL(objectUrl);
      e.target.value = "";
    };
    img.src = objectUrl;
  };

  const moduleSelectOptions = modules.map((m) => ({
    value: m.id,
    label: m.name,
  }));

  return (
    <div className="qb-container">
      <Loader
        isLoading={loading || savingModule || downloadingTemplate}
        message={
          savingModule
            ? "Saving Module..."
            : downloadingTemplate
              ? "Downloading Template..."
              : "Processing..."
        }
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Header Bar */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            {activeTab === "enter" ? (
              <Plus size={26} color="#0d6e5b" />
            ) : (
              <Upload size={26} color="#0d6e5b" />
            )}
            {activeTab === "enter" ? "Add Question" : "Bulk Upload Questions"}
          </h1>
          <p className="qb-subtitle">
            {activeTab === "enter"
              ? "Configure question description, answer choices, optional diagram image, and solution key."
              : "Upload an Excel spreadsheet with multiple choice questions to bulk populate your bank."}
          </p>
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          {/* Main Top Right Mode Toggle: Enter Question vs Upload Questions */}
          <div
            style={{
              display: "flex",
              background: "#f1f5f9",
              padding: "4px",
              borderRadius: "8px",
              border: "1px solid #dde5e2",
            }}
          >
            <button
              type="button"
              className={`qb-btn ${activeTab === "enter" ? "qb-btn-primary" : "qb-btn-outline"}`}
              style={{
                border: "none",
                padding: "6px 16px",
                fontSize: "13px",
                borderRadius: "6px",
              }}
              onClick={() => setActiveTab("enter")}
            >
              <Plus size={14} /> Enter Question
            </button>
            <button
              type="button"
              className={`qb-btn ${activeTab === "upload" ? "qb-btn-primary" : "qb-btn-outline"}`}
              style={{
                border: "none",
                padding: "6px 16px",
                fontSize: "13px",
                borderRadius: "6px",
              }}
              onClick={() => setActiveTab("upload")}
            >
              <Upload size={14} /> Upload Questions
            </button>
          </div>

          {activeTab === "enter" && (
            <button
              type="button"
              className={`qb-btn ${showLivePreview ? "qb-btn-primary" : "qb-btn-outline"}`}
              onClick={() => setShowLivePreview(!showLivePreview)}
            >
              <Eye size={16} />{" "}
              {showLivePreview ? "Hide Live Preview" : "Live Preview"}
            </button>
          )}

          {activeTab === "upload" && (
            <button
              type="button"
              className="qb-btn qb-btn-outline"
              onClick={() => navigate("/dashboard/question-bank/upload-status")}
            >
              <History size={16} /> Upload Logs
            </button>
          )}

          {/* <button
            className="qb-btn qb-btn-outline"
            onClick={() => navigate("/dashboard/question-bank")}
          >
            <ArrowLeft size={16} /> Cancel
          </button> */}
        </div>
      </div>

      {/* TAB 1: ENTER SINGLE QUESTION CONTENT */}
      {activeTab === "enter" ? (
        <div
          className="qb-content-scroll"
          style={{
            display: "grid",
            gridTemplateColumns: showLivePreview ? "1fr 420px" : "1fr",
            gap: "24px",
            transition: "all 0.3s ease",
          }}
        >
          <form onSubmit={handleSingleSubmit} className="qb-single-frame-form">
            <div className="qb-form-body-scroll">
              {/* Section 1: Compact Module & Difficulty Bar */}
              <div className="qb-compact-module-bar">
                <div className="qb-compact-bar-item">
                  <span className="qb-compact-bar-label">
                    Difficulty Level:
                  </span>
                  <div className="qb-difficulty-pills" style={{ gap: "6px" }}>
                    {["Easy", "Medium", "Hard"].map((diff) => (
                      <div
                        key={diff}
                        className={`qb-diff-pill ${difficultyLevel === diff ? `active-${diff.toLowerCase()}` : ""}`}
                        onClick={() => setDifficultyLevel(diff)}
                        style={{ padding: "6px 16px", fontSize: "13px" }}
                      >
                        {diff}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="qb-compact-bar-item" style={{ gap: "10px" }}>
                  <span className="qb-compact-bar-label">
                    Target Module <span style={{ color: "#ef4444" }}>*</span>:
                  </span>
                  {modules.length > 0 ? (
                    <>
                      <div style={{ width: "220px" }}>
                        <CustomSelect
                          options={moduleSelectOptions}
                          value={moduleId}
                          onChange={(val) => {
                            setModuleId(val);
                            const m = modules.find((item) => item.id === val);
                            if (m) setModuleName(m.name);
                          }}
                          placeholder="Select Module..."
                        />
                      </div>
                      <button
                        type="button"
                        className="qb-btn qb-btn-outline"
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => setShowAddModuleModal(true)}
                      >
                        <Plus size={14} /> Add New Module
                      </button>
                    </>
                  ) : (
                    <div
                      style={{
                        padding: "6px 14px",
                        backgroundColor: "#FEF3C7",
                        borderRadius: "8px",
                        color: "#92400E",
                        fontSize: "13px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "16px",
                        minWidth: "260px",
                      }}
                    >
                      <span>No modules found</span>
                      <button
                        type="button"
                        className="qb-btn qb-btn-primary"
                        style={{
                          padding: "4px 12px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => setShowAddModuleModal(true)}
                      >
                        Create Module
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Side-by-Side Question Description & Image Upload (Image 3 Layout) */}
              <div className="qb-desc-image-grid">
                {/* Left Column: Question Description */}
                <div className="qb-desc-card">
                  <label className="qb-desc-label">
                    Question Description{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    className="qb-desc-textarea"
                    placeholder="Enter clear, concise question description..."
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    required
                  />
                </div>

                {/* Right Column: Upload Image (Image 3 Layout Box) */}
                <div className="qb-image-upload-card">
                  <label className="qb-image-upload-label">Upload Image</label>
                  <div className="qb-image-dropzone-box">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      style={{ display: "none" }}
                      id="qb-image-upload-input"
                    />
                    {imagePreviewUrl ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            cursor: "pointer",
                            display: "inline-block",
                          }}
                          onClick={() => setPreviewModalSrc(imagePreviewUrl)}
                          title="Click to view image preview"
                        >
                          <img
                            src={imagePreviewUrl}
                            alt="Preview"
                            className="qb-clickable-preview-img"
                            style={{
                              height: "50px",
                              maxWidth: "100px",
                              objectFit: "contain",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              transition: "all 0.2s ease",
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreviewUrl(null);
                          }}
                          className="qb-btn qb-btn-danger"
                          style={{
                            padding: "4px 10px",
                            fontSize: "12px",
                            marginLeft: "auto",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="qb-image-upload-input"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "100%",
                          cursor: "pointer",
                          textAlign: "center",
                          gap: "4px",
                        }}
                      >
                        <ImageIcon
                          size={22}
                          color="#0d6e5b"
                          style={{ opacity: 0.8 }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#475569",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          Click to upload image asset (optional)
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 3 & 4: Response Options, Concepts & Explanation Container */}
              <div className="qb-response-section-compact">
                {/* Header: Title on Left, Right Answer Select on Top Right */}
                <div className="qb-response-header">
                  <span className="qb-response-title">Response Options</span>
                  <RightAnswerSelect
                    value={correctOption}
                    onChange={setCorrectOption}
                  />
                </div>

                {/* 4-Column Options Grid */}
                <div className="qb-options-grid-4col">
                  {[
                    { key: "A", val: optA, setVal: setOptA },
                    { key: "B", val: optB, setVal: setOptB },
                    { key: "C", val: optC, setVal: setOptC },
                    { key: "D", val: optD, setVal: setOptD },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className={`qb-compact-option-card ${correctOption === item.key ? "active-correct" : ""}`}
                      onClick={() => setCorrectOption(item.key)}
                    >
                      <div className="qb-compact-option-header">
                        <span className="qb-option-letter-badge">
                          {item.key}
                        </span>
                      </div>
                      <textarea
                        className="qb-option-textarea"
                        placeholder={`Option ${item.key} description...`}
                        value={item.val}
                        onChange={(e) => item.setVal(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        required
                      />
                    </div>
                  ))}
                </div>

                {/* Bottom 2-Column Layout: Concepts & Explanation */}
                <div className="qb-bottom-2col-grid">
                  <div className="qb-bottom-col-card">
                    <label className="qb-bottom-col-label">
                      Concepts (Optional)
                    </label>
                    <textarea
                      className="qb-bottom-textarea"
                      placeholder="Concept-1, Concept-2"
                      value={concepts}
                      onChange={(e) => setConcepts(e.target.value)}
                    />
                  </div>

                  <div className="qb-bottom-col-card">
                    <label className="qb-bottom-col-label">
                      Explanation (Optional)
                    </label>
                    <textarea
                      className="qb-bottom-textarea"
                      placeholder="Explanation for the correct answer..."
                      value={explanation}
                      onChange={(e) => setExplanation(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pinned Fixed Bottom Action Bar */}
            <div className="qb-fixed-action-bar">
              <button
                type="button"
                className="qb-btn qb-btn-outline"
                onClick={() => navigate("/dashboard/question-bank")}
                style={{ padding: "8px 20px" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="qb-btn qb-btn-primary"
                style={{ padding: "8px 24px" }}
                disabled={!isSingleFormValid || loading}
              >
                Save Question
              </button>
            </div>
          </form>

          {/* Live Preview Sidebar Card */}
          {showLivePreview && (
            <div
              className="qb-preview-panel"
              style={{ position: "sticky", top: "24px", height: "fit-content" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  paddingBottom: "12px",
                  borderBottom: "1px solid #cbd5e1",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#07473b",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Eye size={18} /> Candidate Preview
                </h3>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: "#0d6e5b",
                    color: "#ffffff",
                  }}
                >
                  LIVE
                </span>
              </div>

              <div
                style={{ display: "flex", gap: "8px", marginBottom: "12px" }}
              >
                <span
                  className={`qb-badge qb-badge-${difficultyLevel.toLowerCase()}`}
                  style={{ fontSize: "11px" }}
                >
                  {difficultyLevel}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginLeft: "auto",
                  }}
                >
                  {moduleName || "Module"}
                </span>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "14px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  marginBottom: "16px",
                  overflow: "hidden",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#0f172a",
                    margin: "0 0 8px 0",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {questionText ||
                    "Your question description will appear here..."}
                </h4>
                {imagePreviewUrl && (
                  <div style={{ textAlign: "center", marginTop: "10px" }}>
                    <img
                      src={imagePreviewUrl}
                      alt="Preview asset"
                      style={{
                        maxHeight: "120px",
                        maxWidth: "100%",
                        width: "auto",
                        borderRadius: "6px",
                        objectFit: "contain",
                        display: "inline-block",
                        border: "1px solid #e2e8f0",
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {[
                  { key: "A", text: optA },
                  { key: "B", text: optB },
                  { key: "C", text: optC },
                  { key: "D", text: optD },
                ]
                  .filter((o) => o.text.trim())
                  .map((opt) => (
                    <div
                      key={opt.key}
                      style={{
                        padding: "10px 14px",
                        borderRadius: "8px",
                        background:
                          correctOption === opt.key ? "#e6f4f1" : "#ffffff",
                        border:
                          correctOption === opt.key
                            ? "1.5px solid #0d6e5b"
                            : "1px solid #e2e8f0",
                        fontSize: "13px",
                        fontWeight: correctOption === opt.key ? "600" : "400",
                        color:
                          correctOption === opt.key ? "#07473b" : "#334155",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      <strong>{opt.key}.</strong> {opt.text}
                    </div>
                  ))}
              </div>

              {explanation && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    background: "#fffbeb",
                    borderRadius: "8px",
                    border: "1px solid #fef3c7",
                    fontSize: "12px",
                    color: "#92400e",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  <strong>Solution Note:</strong> {explanation}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* TAB 2: BULK EXCEL UPLOAD CONTENT */
        <div
          className="qb-content-scroll"
          style={{ display: "flex", flexDirection: "column", gap: "12px" }}
        >
          <form onSubmit={handleBulkUploadSubmit}>
            {/* Step 1: Module Selector & Template Download */}
            <div className="qb-form-section" style={{ padding: "16px 20px" }}>
              <div
                className="qb-section-header"
                style={{ marginBottom: "12px", paddingBottom: "8px" }}
              >
                <FileText size={18} color="#0d6e5b" />
                <span>1. Select Module & Download Excel Template</span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "6px",
                    }}
                  >
                    <label className="qb-label" style={{ margin: 0 }}>
                      Target Module <span style={{ color: "#ef4444" }}>*</span>:
                    </label>
                    {modules.length > 0 && (
                      <button
                        type="button"
                        className="qb-btn qb-btn-outline"
                        style={{
                          padding: "4px 10px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => setShowAddModuleModal(true)}
                      >
                        <Plus size={14} /> Add New Module
                      </button>
                    )}
                  </div>
                  {modules.length > 0 ? (
                    <CustomSelect
                      options={moduleSelectOptions}
                      value={moduleId}
                      onChange={(val) => {
                        setModuleId(val);
                        const m = modules.find((item) => item.id === val);
                        if (m) setModuleName(m.name);
                      }}
                      placeholder="Select Module..."
                    />
                  ) : (
                    <div
                      style={{
                        padding: "10px 14px",
                        backgroundColor: "#FEF3C7",
                        borderRadius: "8px",
                        color: "#92400E",
                        fontSize: "13px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>No modules found</span>
                      <button
                        type="button"
                        className="qb-btn qb-btn-primary"
                        style={{ padding: "4px 12px", fontSize: "12px" }}
                        onClick={() => setShowAddModuleModal(true)}
                      >
                        Create Module
                      </button>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #dde5e2",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 4px",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#07473b",
                    }}
                  >
                    Question Format Templates
                  </h4>
                  <p
                    style={{
                      margin: "0 0 8px",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    Download the standardized multiple-choice template formatted
                    for bulk question imports.
                  </p>
                  <div
                    style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  >
                    <button
                      type="button"
                      className="qb-btn qb-btn-secondary"
                      style={{ padding: "6px 12px", fontSize: "12px" }}
                      onClick={() => handleDownloadTemplate("excel")}
                      disabled={downloadingTemplate}
                    >
                      <Download size={14} /> Download Excel (.xlsx)
                    </button>
                    <button
                      type="button"
                      className="qb-btn qb-btn-secondary"
                      style={{ padding: "6px 12px", fontSize: "12px" }}
                      onClick={() => handleDownloadTemplate("csv")}
                      disabled={downloadingTemplate}
                    >
                      <Download size={14} /> Download CSV (.csv)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Excel File Dropzone */}
            <div
              className="qb-form-section"
              style={{ padding: "20px 24px", marginTop: "12px" }}
            >
              <div
                className="qb-section-header"
                style={{ marginBottom: "14px", paddingBottom: "8px" }}
              >
                <Upload size={18} color="#0d6e5b" />
                <span>2. Upload Question File</span>
              </div>

              <div
                style={{
                  border: "2px dashed #dde5e2",
                  borderRadius: "12px",
                  padding: "36px 24px",
                  minHeight: "150px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onClick={() =>
                  document.getElementById("qb-bulk-excel-input")?.click()
                }
              >
                <Upload
                  size={36}
                  color="#0d6e5b"
                  style={{ marginBottom: "8px" }}
                />
                <h4
                  style={{
                    margin: "0 0 4px",
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#1e293b",
                  }}
                >
                  {selectedExcelFile
                    ? selectedExcelFile.name
                    : "Click or drop Excel spreadsheet here"}
                </h4>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                  {selectedExcelFile
                    ? `${(selectedExcelFile.size / 1024).toFixed(2)} KB`
                    : "Supports .xlsx, .csv spreadsheets up to 10MB"}
                </p>
                <input
                  id="qb-bulk-excel-input"
                  type="file"
                  accept=".xlsx,.csv"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedExcelFile(e.target.files[0]);
                    }
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "14px",
                }}
              >
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  onClick={() => navigate("/dashboard/question-bank")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="qb-btn qb-btn-primary"
                  style={{ padding: "8px 24px" }}
                  disabled={!isBulkUploadValid || loading}
                >
                  Upload & Import Questions
                </button>
              </div>
            </div>
          </form>

          {/* Upload Processing Summary Result */}
          {uploadResult && (
            <div className="qb-form-section">
              <div className="qb-section-header" style={{ color: "#15803d" }}>
                <CheckCircle size={20} color="#15803d" />
                <span>Batch Upload Processing Results</span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    padding: "14px",
                    borderRadius: "8px",
                    border: "1px solid #dde5e2",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: 600,
                    }}
                  >
                    Total Rows
                  </span>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#1e293b",
                      marginTop: "2px",
                    }}
                  >
                    {uploadResult.total_questions || 0}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#f0fdf4",
                    padding: "14px",
                    borderRadius: "8px",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#15803d",
                      fontWeight: 600,
                    }}
                  >
                    Successfully Imported
                  </span>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#15803d",
                      marginTop: "2px",
                    }}
                  >
                    {uploadResult.success_count || 0}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#fffbeb",
                    padding: "14px",
                    borderRadius: "8px",
                    border: "1px solid #fef3c7",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#b45309",
                      fontWeight: 600,
                    }}
                  >
                    Skipped Duplicates
                  </span>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#b45309",
                      marginTop: "2px",
                    }}
                  >
                    {uploadResult.skipped_duplicates || 0}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    padding: "14px",
                    borderRadius: "8px",
                    border: "1px solid #fecaca",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#b91c1c",
                      fontWeight: 600,
                    }}
                  >
                    Validation Errors
                  </span>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#b91c1c",
                      marginTop: "2px",
                    }}
                  >
                    {uploadResult.failed_count || 0}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  className="qb-btn qb-btn-primary"
                  onClick={() => navigate("/dashboard/question-bank")}
                >
                  Done & View Question
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add New Module Modal */}
      {showAddModuleModal &&
        ReactDOM.createPortal(
          <div
            className="qb-modal-overlay"
            onClick={() => setShowAddModuleModal(false)}
          >
            <div
              className="qb-modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "480px" }}
            >
              <div className="qb-modal-header">
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#07473b",
                  }}
                >
                  Create New Module
                </h3>
                <button
                  className="qb-modal-close"
                  onClick={() => setShowAddModuleModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateModule}>
                <div className="qb-form-group">
                  <label className="qb-label">
                    Module Name <span style={{ color: "#ef4444" }}>*</span>:
                  </label>
                  <input
                    type="text"
                    className="qb-input"
                    placeholder="e.g. System Design, Cloud Computing, Algorithms"
                    value={newModuleName}
                    onChange={(e) => setNewModuleName(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="qb-btn qb-btn-outline"
                    onClick={() => setShowAddModuleModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="qb-btn qb-btn-primary">
                    Create Module
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
      {/* Image Preview Lightbox Modal */}
      <ImagePreviewModal
        src={previewModalSrc}
        onClose={() => setPreviewModalSrc(null)}
      />
    </div>
  );
};

export default AddQuestionPage;
